import { watch } from "vue";
import { useRoute } from "vue-router";
import { useOpenDrawer } from "./drawerHelper";

const DRAWER_STAGGER_DELAY_MS = 100;

/**
 * Watches `route.query.sidebar` and renders drawer components for any
 * newly added sidebar names. Handles both single pushes (immediate) and
 * page-refresh restores (staggered so each drawer mounts before the next).
 *
 * Call once in App.vue setup to activate the sidebar → drawer bridge.
 */
export const useSidebarWatcher = () => {
  const route = useRoute();
  const { openSidebar } = useOpenDrawer();

  let previousSidebarNames: string[] = [];

  watch(
    () => route.query.sidebar,
    (newSidebar) => {
      if (typeof newSidebar !== "string") {
        previousSidebarNames = [];
        return;
      }

      const newNames = newSidebar.split(",").filter(Boolean);
      const toOpen = newNames.filter(
        (name) => !previousSidebarNames.includes(name),
      );

      previousSidebarNames = newNames;

      if (toOpen.length === 0) {
        return;
      }

      // Single new drawer (normal push): open immediately
      if (toOpen.length === 1) {
        openSidebar(toOpen[0]);
        return;
      }

      // Multiple new drawers (page refresh): open sequentially bottom → top
      // so each drawer mounts and acquires its z-index before the next opens.
      toOpen.forEach((name, index) => {
        setTimeout(() => openSidebar(name), index * DRAWER_STAGGER_DELAY_MS);
      });
    },
    { immediate: true },
  );
};
